import { useQuery } from "@tanstack/react-query";

import { plural } from "../../lib/format";
import { SellerQueryError, threadListQueryOptions } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { groupThreads, STAGE_META } from "../../lib/talent-stage";
import { SellerErrorState } from "../seller/seller-error-state";
import { InboxIcon } from "./icons";
import { ProjectCard } from "./project-card";

// The talent's page: every project Mira brought them, stacked one on top of the other.
// Active first (the ones waiting on them at the top, then the most recent), then the past
// ones, muted. The conversation is not here: a card's "Open inbox" opens it in the side
// panel (`store.selectedThreadId`). Server state only (the thread list IS the talent's
// inbox; each row carries the brief at a glance, so no per-card read).
export function ProjectList() {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const selectedThreadId = useSellerStore((s) => s.selectedThreadId);
  const setSelectedThread = useSellerStore((s) => s.setSelectedThread);
  const q = useQuery(threadListQueryOptions(sellerId));

  if (q.isPending) {
    return (
      <div className="sl-projects" aria-busy="true" aria-label="Loading your projects">
        <span className="sl-skel sl-skel--title" />
        {[0, 1, 2].map((i) => (
          <span key={i} className="sl-skel sl-skel--card" />
        ))}
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="sl-projects">
        <SellerErrorState
          title="Couldn't load your projects"
          message={q.error instanceof SellerQueryError ? q.error.message : "Something went wrong."}
          onRetry={() => void q.refetch()}
        />
      </div>
    );
  }

  const { active, past } = groupThreads(q.data.threads);
  const waiting = active.filter((t) => STAGE_META[t.stage].needsYou).length;
  const lede =
    active.length + past.length === 0
      ? "Mira has not reached out about a project yet. New projects show up here."
      : [
          plural(active.length, "active project"),
          waiting > 0 ? `${waiting} waiting on you` : "nothing waiting on you",
        ].join(" · ");

  return (
    <div className="sl-projects">
      <header className="sl-projects__head">
        <h1 className="sl-projects__title">Your projects</h1>
        <p className="sl-projects__lede">{lede}</p>
      </header>
      {active.length + past.length === 0 ? (
        <div className="sl-home__empty">
          <InboxIcon size={22} />
          <span>Your projects will appear here.</span>
        </div>
      ) : null}
      {active.length > 0 ? (
        <section className="sl-projects__group" aria-label="Active projects">
          {active.map((t, i) => (
            <ProjectCard
              key={t.thread_id}
              thread={t}
              index={i}
              open={t.thread_id === selectedThreadId}
              onOpen={setSelectedThread}
            />
          ))}
        </section>
      ) : null}
      {past.length > 0 ? (
        <section className="sl-projects__group" aria-labelledby="sl-past-title">
          <h2 id="sl-past-title" className="sl-home__section-title">
            Past projects
          </h2>
          {past.map((t, i) => (
            <ProjectCard
              key={t.thread_id}
              thread={t}
              index={active.length + i}
              open={t.thread_id === selectedThreadId}
              onOpen={setSelectedThread}
              muted
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}
